const utils = {
	debounce,
	formatDateRange,
	imageToBase64,
	memoize,
	isJSON, // f(value) => boolean
	isAbsoluteUrl, // f(value) => boolean
};

export default utils;

/**
 * Returns a (debounced) function, which, if it is called continuously it will not get triggered.
 * The function will be executed after it stops being called for "wait" milliseconds.
 * @param {function} fn - the function to be debounced
 * @param {number} waitFor - waiting time in milliseconds until the debounced function is triggerable again
 * @return the debounced function
 */
export function debounce(fn: Function, waitFor: number) {
	let timeout: any;

	return function (...args: any) {
		const delayedFn = () => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			clearTimeout(timeout);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			fn(...args);
		};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		clearTimeout(timeout);
		timeout = setTimeout(delayedFn, waitFor);
	};
}

/**
 * Format a date range based on the provided start and end dates
 * ex output: Jan 12 - 18, 2024
 * @param start - yyyy-mm-dd format
 * @param end - yyyy-mm-dd format
 * @returns the formatted date range
 */
export function formatDateRange(start: string, end: string): string {
	const formatter = new Intl.DateTimeFormat("en", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});

	if (start && end) {
		return formatter.formatRange(new Date(start), new Date(end));
	}

	if (start || end) {
		return formatter.format(start ? new Date(start) : new Date(end));
	}

	return "";
}

/**
 * Convert an image file with base64 format for preview
 * @param {File} file
 * @param {Function} cb Function to call with the encoded image when the process is finished
 */
export function imageToBase64(file: File, cb: Function) {
	const reader = new FileReader();

	reader.onloadend = () => {
		cb(reader.result?.toString() ?? "");
	};

	reader.readAsDataURL(file);
}

/**
 * Memoize a function
 * Example usage:
 * // fn(2) => 2, fn(3) => 6, fn(4) => 24 ...
 * const fn = (n) => Array(n).fill(1).reduce((out, _, index) => out * (index + 1), 1);
 * const memoizedFn = memoize(fn);
 * => memoizedFn(n)
 * @param fn
 * @returns The memoized fn function
 */
export function memoize<T>(fn: Function) {
	const cache = new Map();

	return (arg: any): T => {
		return cache.has(arg)
			? cache.get(arg)
			: cache.set(arg, fn(arg)) && cache.get(arg);
	};
}

/**
 * Check if the given arg is a valid JSON (passes JSON.parse)
 * @param str
 */
export function isJSON(str: any) {
	try {
		return Boolean(JSON.parse(str as string) && Boolean(str));
	} catch (e) {
		return false;
	}
}

/**
 * Check if the given string is an absolute HTTP URL
 * @param string
 */
export function isAbsoluteUrl(string: string) {
	let url: URL;

	try {
		url = new URL(string);
	} catch (_) {
		return false;
	}

	return url.protocol === "http:" || url.protocol === "https:";
}
